import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import './index.css';
import './pages/ScrollablePage.css';
import { reportWebVitals } from './utils/reportWebVitals';
import { runMinimumBuildBootstrap } from './bootstrap/minimumBuild';
import { captureAccessNotificationVerificationCapability } from './services/accessNotificationVerificationCapability';
import {
  captureAccessUrlCapabilities,
  redactAccessCapabilitiesForTelemetry,
} from './services/accessUrlCapabilities';

captureAccessNotificationVerificationCapability();
captureAccessUrlCapabilities();

const isSharedTreeCapabilityRoute = /^\/tree\/shared\/[^/]+(?:\/|$)/.test(window.location.pathname);
const isSensitiveAdministrationRoute = /^\/access-admin\/?$/.test(window.location.pathname);
const isReplayExcludedRoute = isSharedTreeCapabilityRoute || isSensitiveAdministrationRoute;

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.MODE,
  enabled: import.meta.env.PROD && !!import.meta.env.VITE_SENTRY_DSN,
  integrations: [
    Sentry.browserTracingIntegration(),
    ...(!isReplayExcludedRoute
      ? [Sentry.replayIntegration({
          maskAllText: false,
          blockAllMedia: false,
          beforeAddRecordingEvent: (event) => redactAccessCapabilitiesForTelemetry(event),
        })]
      : []),
  ],
  beforeSend: (event) => redactAccessCapabilitiesForTelemetry(event),
  beforeSendTransaction: (event) => redactAccessCapabilitiesForTelemetry(event),
  tracesSampleRate: 0.1,
  replaysSessionSampleRate: 0.05,
  replaysOnErrorSampleRate: 1.0,
  ignoreErrors: [
    'vite:preloadError',
    'ResizeObserver loop limit exceeded',
    'Non-Error promise rejection captured',
  ],
});

// After a new deployment the server's JS chunks have new content-hash names.
// If a user's browser has a cached old index.html that references old chunk names,
// dynamic imports fail with a MIME-type error (server returns index.html fallback
// instead of the missing .js file). Vite emits 'vite:preloadError' in this case.
// One automatic reload fetches the fresh index.html + new chunks, resolving it.
// A sessionStorage flag prevents an infinite reload loop if the error persists.
window.addEventListener('vite:preloadError', () => {
  if (!sessionStorage.getItem('vite_reload_attempted')) {
    sessionStorage.setItem('vite_reload_attempted', '1');
    window.location.reload();
  }
});

async function bootstrap(): Promise<void> {
  runMinimumBuildBootstrap();
  const { default: App } = await import('./App');
  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );
  reportWebVitals();
}

void bootstrap();
